import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { VHU_LOTS_PAGE } from "../../routes/routeConstants";
import { isVehiclePhoto, resolveDocumentUrl } from "../database/documentUrl";
import { search as searchVehicules } from "../database/vehiculeService";
import { loadVhuLot, saveVhuLot } from "./vhuLotService";


const STATUS_OPTIONS = [
  { value: "BROUILLON", label: "Brouillon" },
  { value: "OUVERT", label: "Ouvert" },
  { value: "CLOTURE", label: "Cloture" },
  { value: "ANNULE", label: "Annule" },
];

const toDatetimeLocal = (date) => {
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const normalizeDatetimeLocal = (value) => {
  if (!value) return "";
  const valueText = String(value);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(valueText)) return valueText;
  const date = new Date(valueText);
  if (Number.isNaN(date.getTime())) return valueText;
  return toDatetimeLocal(date);
};

const createEmptyForm = () => {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);

  return {
    reference: "",
    titre: "",
    description: "",
    status: "OUVERT",
    dateDebut: toDatetimeLocal(now),
    dateFin: toDatetimeLocal(tomorrow),
    prixDepart: "",
    pasMinimal: "0",
    estimationDepart: "",
    lieuStockage: "",
    vehiculeIds: [],
    isActif: true,
  };
};

const asNumberOrNull = (value) => (value === "" || value === null || value === undefined ? null : Number(value));
const vehicleLabel = (vehicle) => [vehicle.marque, vehicle.modele, vehicle.immatriculation || vehicle.numChassis].filter(Boolean).join(" - ") || `Vehicule #${vehicle.id}`;
const vehiclePhotos = (vehicle) => ((vehicle?.photos || vehicle?.documents || []).filter(isVehiclePhoto));
const moneyLabel = (value) => `${Number(value || 0).toLocaleString("fr-FR")} MAD`;

const normalizeLotForForm = (lot) => ({
  ...createEmptyForm(),
  ...(lot || {}),
  dateDebut: lot ? normalizeDatetimeLocal(lot.dateDebut) : createEmptyForm().dateDebut,
  dateFin: lot ? normalizeDatetimeLocal(lot.dateFin) : createEmptyForm().dateFin,
  prixDepart: lot?.prixDepart ?? "",
  pasMinimal: lot?.pasMinimal ?? "0",
  estimationDepart: lot?.estimationDepart ?? "",
  vehiculeIds: lot?.vehiculeIds || lot?.vehicules?.map((vehicle) => vehicle.id) || [],
  vehiclePricing: Object.fromEntries((lot?.vehicules || []).map(vehicle => [vehicle.id, {
    prixDepart: vehicle.prixDepart ?? lot?.prixDepart ?? "",
    estimationDepart: vehicle.estimationDepart ?? lot?.estimationDepart ?? "",
  }])),
  isActif: lot?.isActif !== false,
});

function VhuLotFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [form, setForm] = useState(() => normalizeLotForForm(location.state?.lot));
  const [vehicules, setVehicules] = useState([]);
  const [vehicleQuery, setVehicleQuery] = useState("");
  const [loading, setLoading] = useState(Boolean(id));
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      setLoadFailed(false);
      setError(null);
      try {
        const requests = [searchVehicules({ criteria: {}, page: 0, size: 500, sortField: "createdDate", direction: "DESC" })];
        if (id) requests.push(loadVhuLot(id));
        const [vehicleResponse, lotResponse] = await Promise.all(requests);
        if (cancelled) return;
        const lot = lotResponse?.data?.body;
        if (id && !lot) throw new Error("Lot introuvable");
        const available = vehicleResponse?.data?.body?.content || [];
        // Keep selected vehicles even when they are outside the first search page.
        setVehicules([...new Map([...available, ...(lot?.vehicules || [])].map(vehicle => [Number(vehicle.id), vehicle])).values()]);
        setForm(normalizeLotForForm(lot));
      } catch (e) {
        if (cancelled) return;
        setLoadFailed(true);
        setError(e?.response?.data?.message || "Impossible de charger le formulaire du lot.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => { cancelled = true; };
  }, [id]);

  const selectedVehicles = useMemo(() => {
    const selectedIds = new Set((form.vehiculeIds || []).map(Number));
    return vehicules.filter((vehicle) => selectedIds.has(Number(vehicle.id)));
  }, [form.vehiculeIds, vehicules]);

  const filteredVehicules = useMemo(() => {
    const query = vehicleQuery.trim().toLowerCase();
    if (!query) return vehicules;
    return vehicules.filter((vehicle) => vehicleLabel(vehicle).toLowerCase().includes(query));
  }, [vehicules, vehicleQuery]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleVehicle = (idValue) => {
    setForm((prev) => {
      const current = (prev.vehiculeIds || []).map(Number);
      const nextId = Number(idValue);
      return {
        ...prev,
        vehiculeIds: current.includes(nextId) ? current.filter((item) => item !== nextId) : [...current, nextId],
      };
    });
  };

  const setVehiclePrice = (vehicleId, field, value) => setForm(prev => ({
    ...prev,
    vehiclePricing: { ...prev.vehiclePricing, [vehicleId]: { ...prev.vehiclePricing?.[vehicleId], [field]: value } },
  }));
  const total = field => selectedVehicles.reduce((sum, vehicle) => sum + Number(form.vehiclePricing?.[vehicle.id]?.[field] || 0), 0);

  const renderVehicleThumb = (vehicle, size = 48) => {
    const photos = vehiclePhotos(vehicle);
    const photo = photos[0];
    if (!photo) {
      return (
        <span className="vhu-thumb vhu-thumb-empty" style={{ width: size, height: size }}>
          <i className="ri-car-line text-muted" />
        </span>
      );
    }
    return <img src={resolveDocumentUrl(photo)} alt={vehicleLabel(vehicle)} className="vhu-thumb" style={{ width: size, height: size }} />;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving || loadFailed) return;
    if (!form.vehiculeIds.length) {
      setError("Selectionnez au moins un vehicule.");
      return;
    }
    if (form.dateDebut && form.dateFin && form.dateFin <= form.dateDebut) {
      setError("La date de fin doit suivre la date de debut.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveVhuLot({
        ...form,
        reference: undefined,
        id: isEditMode ? Number(id) : form.id,
        vehicules: form.vehiculeIds.map(vehicleId => ({
          id: Number(vehicleId),
          prixDepart: asNumberOrNull(form.vehiclePricing?.[vehicleId]?.prixDepart) ?? 0,
          estimationDepart: asNumberOrNull(form.vehiclePricing?.[vehicleId]?.estimationDepart) ?? 0,
        })),
        prixDepart: total("prixDepart"),
        pasMinimal: asNumberOrNull(form.pasMinimal),
        estimationDepart: total("estimationDepart"),
      });
      navigate(VHU_LOTS_PAGE, {
        state: { successMessage: isEditMode ? "Lot VHU mis a jour avec succes." : "Lot VHU cree avec succes." },
      });
    } catch (e) {
      setError(e?.response?.data?.message || "Impossible d'enregistrer le lot VHU.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content auction-page vhu-lots-page vhu-lot-form-page">
      <Container fluid>
        <Breadcrumbs
          title={isEditMode ? "Modifier le lot VHU" : "Nouveau lot VHU"}
          breadcrumbItems={[{ title: "Modules", link: "#" }, { title: "Lots VHU", link: VHU_LOTS_PAGE }, { title: isEditMode ? "Modification" : "Creation", link: "#" }]}
        />

        {error && <Alert color="danger">{error}</Alert>}
        {loading ? <div className="text-center py-4"><Spinner color="primary" /></div> : (
          <form onSubmit={submit}>
            <fieldset disabled={saving || loadFailed}>
              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="p-3">
                  <Row className="g-2">
                    <Col md={3}><Label for="vhu-lot-reference">Référence</Label><Input bsSize="sm" id="vhu-lot-reference" value={form.reference || ""} readOnly placeholder="Générée à l'enregistrement" /></Col>
                    <Col md={6}><Label for="titre">Titre</Label><Input bsSize="sm" id="titre" required value={form.titre || ""} onChange={e => setField("titre", e.target.value)} /></Col>
                    <Col md={3}><Label for="vhu-lot-status">Statut</Label><Input bsSize="sm" id="vhu-lot-status" type="select" value={form.status} onChange={e => setField("status", e.target.value)}>{STATUS_OPTIONS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</Input></Col>
                    <Col md={3}><Label for="dateDebut">Début</Label><Input bsSize="sm" id="dateDebut" type="datetime-local" value={form.dateDebut} onChange={e => setField("dateDebut", e.target.value)} /></Col>
                    <Col md={3}><Label for="dateFin">Fin</Label><Input bsSize="sm" id="dateFin" type="datetime-local" value={form.dateFin} onChange={e => setField("dateFin", e.target.value)} /></Col>
                    <Col md={4}><Label for="lieuStockage">Lieu de stockage</Label><Input bsSize="sm" id="lieuStockage" value={form.lieuStockage || ""} onChange={e => setField("lieuStockage", e.target.value)} /></Col>
                    <Col md={2}><Label for="pasMinimal">Pas minimal (MAD)</Label><Input bsSize="sm" id="pasMinimal" type="number" min="0" step="0.01" value={form.pasMinimal} onChange={e => setField("pasMinimal", e.target.value)} /></Col>
                    <Col md={9}><Label for="description">Description</Label><Input bsSize="sm" id="description" type="textarea" rows={2} maxLength={2000} value={form.description || ""} onChange={e => setField("description", e.target.value)} /></Col>
                    <Col md={3} className="d-flex align-items-center"><Label check className="mb-0"><Input type="checkbox" checked={form.isActif} onChange={e => setField("isActif", e.target.checked)} /> Visible sur mobile</Label></Col>
                  </Row>
                </CardBody>
              </Card>
              <Row className="g-3">
                <Col lg={4}>
                  <Card className="border-0 shadow-sm"><CardBody className="p-3">
                    <h6>Ajouter des véhicules</h6>
                    <Input bsSize="sm" aria-label="Rechercher un véhicule" placeholder="Marque, modèle, immatriculation…" value={vehicleQuery} onChange={e => setVehicleQuery(e.target.value)} />
                    <div className="mt-2" style={{ maxHeight: 350, overflowY: "auto" }}>
                      {filteredVehicules.map(vehicle => {
                        const selected = form.vehiculeIds.map(Number).includes(Number(vehicle.id));
                        return <button type="button" key={vehicle.id} className={`vhu-vehicle-option w-100 p-2 ${selected ? "active" : ""}`} aria-pressed={selected} onClick={() => toggleVehicle(vehicle.id)}>
                          <span className="d-flex gap-2 align-items-center">{renderVehicleThumb(vehicle, 34)}<span className="flex-grow-1 text-start small">{vehicleLabel(vehicle)}</span><i className={selected ? "ri-checkbox-circle-fill text-primary" : "ri-add-circle-line"} /></span>
                        </button>;
                      })}
                      {!filteredVehicules.length && <p className="text-muted small mt-2">Aucun véhicule trouvé.</p>}
                    </div>
                  </CardBody></Card>
                </Col>
                <Col lg={8}>
                  <Card className="border-0 shadow-sm"><CardBody className="p-3">
                    <h6>Véhicules sélectionnés ({selectedVehicles.length})</h6>
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-2">
                        <thead><tr><th>Véhicule</th><th style={{ width: "24%" }}>Prix de départ (MAD)</th><th style={{ width: "24%" }}>Estimation (MAD)</th><th /></tr></thead>
                        <tbody>{selectedVehicles.map(vehicle => <tr key={vehicle.id}>
                          <td><span className="small">{vehicleLabel(vehicle)}</span></td>
                          {["prixDepart", "estimationDepart"].map(field => <td key={field}><Input bsSize="sm" type="number" min="0" step="0.01" required aria-label={`${field === "prixDepart" ? "Prix de départ" : "Estimation"} ${vehicleLabel(vehicle)}`} value={form.vehiclePricing?.[vehicle.id]?.[field] ?? ""} onChange={e => setVehiclePrice(vehicle.id, field, e.target.value)} /></td>)}
                          <td><Button type="button" size="sm" color="light" aria-label={`Retirer ${vehicleLabel(vehicle)}`} onClick={() => toggleVehicle(vehicle.id)}><i className="ri-close-line" /></Button></td>
                        </tr>)}</tbody>
                      </table>
                    </div>
                    {!selectedVehicles.length && <p className="text-muted small">Sélectionnez les véhicules à gauche, puis renseignez leurs montants.</p>}
                    <div className="d-flex flex-wrap gap-3 small border-top pt-2"><span>Total départ : <strong>{moneyLabel(total("prixDepart"))}</strong></span><span>Total estimation : <strong>{moneyLabel(total("estimationDepart"))}</strong></span></div>
                  </CardBody></Card>
                </Col>
              </Row>
            </fieldset>
            <div className="d-flex justify-content-end gap-2 pb-3">
              <Button color="light" type="button" onClick={() => navigate(VHU_LOTS_PAGE)}>Annuler</Button>
              <Button color="primary" type="submit" disabled={saving || loadFailed}>{saving && <Spinner size="sm" className="me-2" />}{isEditMode ? "Enregistrer les modifications" : "Créer le lot"}</Button>
            </div>
          </form>
        )}
      </Container>
    </div>
  );
}

export default VhuLotFormPage;
